import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { roleCenterIcon } from './roleCenterIcons';
import { MAX_REASON_LENGTH, reasonError, type RoleView } from './roleCenterApi';
import type { ChangeLine } from './roleCenterRules';

/**
 * Okna centrum ról (decyzja użytkownika 22.10.2026, S-22.10-01): zapis
 * zmian z listą „było → jest” i obowiązkowym powodem, nowa / edytowana rola,
 * usunięcie roli własnej. Powód trafia do historii roli i dziennika działań.
 */

/** Pole powodu — wspólne dla wszystkich okien. */
const ReasonField = ({
  value,
  onChange,
  touched,
  testId,
}: {
  value: string;
  onChange: (v: string) => void;
  touched: boolean;
  testId: string;
}) => {
  const error = touched ? reasonError(value) : null;
  return (
    <TextField
      label="Powód zmiany"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required
      fullWidth
      multiline
      minRows={2}
      error={Boolean(error)}
      helperText={error ?? 'Np. „decyzja z 22.10 — planista bez obsadzania”. Trafi do historii i dziennika działań.'}
      slotProps={{ htmlInput: { maxLength: MAX_REASON_LENGTH, 'data-testid': testId } }}
    />
  );
};

/** Okno zapisu zmian uprawnień i widoczności. */
export const SaveChangesDialog = ({
  open,
  role,
  lines,
  saving,
  error,
  onClose,
  onSave,
}: {
  open: boolean;
  role: RoleView;
  lines: ChangeLine[];
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (reason: string) => void;
}) => {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const submit = () => {
    setTouched(true);
    if (reasonError(reason) === null) onSave(reason.trim());
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="role-save-dialog">
      <DialogTitle>Zapisać zmiany roli „{role.name}”?</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none' }} data-testid="role-changes-list">
            {lines.map((l) => (
              <Box
                component="li"
                key={`${l.kind}-${l.key}`}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, flexWrap: 'wrap' }}
                data-testid={`role-change-${l.key}`}
              >
                <Chip
                  size="small"
                  variant="outlined"
                  label={l.kind === 'permission' ? 'uprawnienie' : 'widoczność'}
                />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {l.name}:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {l.from}
                </Typography>
                <Typography variant="body2" aria-hidden>
                  →
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {l.to}
                </Typography>
              </Box>
            ))}
          </Box>
          <ReasonField value={reason} onChange={setReason} touched={touched} testId="role-save-reason" />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Anuluj
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving} data-testid="role-save-confirm">
          Zapisz zmiany
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export type RoleFormValues = {
  name: string;
  description: string;
  icon: string;
  active: boolean;
  copyFrom: string;
};

/** Nowa rola albo zmiana nazwy, opisu, ikony i aktywności. */
export const RoleFormDialog = ({
  open,
  mode,
  role,
  roles,
  icons,
  saving,
  error,
  onClose,
  onSave,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  role?: RoleView;
  roles: RoleView[];
  icons: string[];
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (values: RoleFormValues, reason: string) => void;
}) => {
  const [values, setValues] = useState<RoleFormValues>({
    name: role?.name ?? '',
    description: role?.description ?? '',
    icon: role?.icon ?? 'Badge',
    active: role?.active ?? true,
    copyFrom: '',
  });
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const nameError = touched && values.name.trim().length < 3 ? 'Nazwa roli: co najmniej 3 znaki' : null;
  const system = role?.system === true;
  const submit = () => {
    setTouched(true);
    if (reasonError(reason) === null && values.name.trim().length >= 3) onSave(values, reason.trim());
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="role-form-dialog">
      <DialogTitle>{mode === 'create' ? 'Nowa rola' : `Rola „${role?.name}”`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Nazwa roli"
            value={values.name}
            onChange={(e) => setValues({ ...values, name: e.target.value })}
            disabled={system}
            required
            error={Boolean(nameError)}
            helperText={
              nameError ?? (system ? 'Nazwy roli systemowej nie zmienia się — stoi w dokumentach procesu.' : undefined)
            }
            slotProps={{ htmlInput: { maxLength: 100, 'data-testid': 'role-form-name' } }}
          />
          <TextField
            label="Opis — co ta rola robi"
            value={values.description}
            onChange={(e) => setValues({ ...values, description: e.target.value })}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 1000, 'data-testid': 'role-form-description' } }}
          />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Ikona
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={values.icon}
              onChange={(_, v: string | null) => v && setValues({ ...values, icon: v })}
              sx={{ flexWrap: 'wrap', display: 'flex' }}
              data-testid="role-form-icons"
            >
              {icons.map((i, n) => (
                <ToggleButton key={i} value={i} aria-label={`ikona ${n + 1}`} sx={{ p: 0.75 }}>
                  {roleCenterIcon(i)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          {mode === 'create' ? (
            <TextField
              select
              label="Na wzór roli (uprawnienia i widoczność)"
              value={values.copyFrom}
              onChange={(e) => setValues({ ...values, copyFrom: e.target.value })}
              helperText="Pusta — rola bez uprawnień; przełączniki ustawisz na karcie roli."
              slotProps={{ htmlInput: { 'data-testid': 'role-form-copy' } }}
            >
              <MenuItem value="">bez wzoru</MenuItem>
              {roles
                .filter((r) => r.id !== 'API_CLIENT')
                .map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
            </TextField>
          ) : null}
          {mode === 'edit' && !system ? (
            <Tooltip title={role && role.userCount > 0 ? 'Rolę mają konta — najpierw nadaj im inną rolę' : ''}>
              <FormControlLabel
                control={
                  <Switch
                    checked={values.active}
                    onChange={(e) => setValues({ ...values, active: e.target.checked })}
                    disabled={Boolean(role && role.userCount > 0 && values.active)}
                  />
                }
                label="Rola aktywna (można ją nadać kontom)"
              />
            </Tooltip>
          ) : null}
          <ReasonField value={reason} onChange={setReason} touched={touched} testId="role-form-reason" />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Anuluj
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving} data-testid="role-form-save">
          {mode === 'create' ? 'Dodaj rolę' : 'Zapisz'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Usunięcie roli własnej (bez kont) — z powodem. */
export const DeleteRoleDialog = ({
  open,
  role,
  saving,
  error,
  onClose,
  onDelete,
}: {
  open: boolean;
  role: RoleView;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onDelete: (reason: string) => void;
}) => {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="role-delete-dialog">
      <DialogTitle>Usunąć rolę „{role.name}”?</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            Rola zniknie z listy, a jej uprawnienia i ustawienia widoczności — razem z nią. Historia zmian zostaje.
          </Typography>
          <ReasonField value={reason} onChange={setReason} touched={touched} testId="role-delete-reason" />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Anuluj
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={saving}
          onClick={() => {
            setTouched(true);
            if (reasonError(reason) === null) onDelete(reason.trim());
          }}
          data-testid="role-delete-confirm"
        >
          Usuń rolę
        </Button>
      </DialogActions>
    </Dialog>
  );
};
